import { useCallback, useState } from 'react'

/**
 * Label rolls the label printer can be loaded with. The browser cannot ask the
 * printer what is loaded, so the operator picks it once; the choice is kept
 * per computer in localStorage and shared by every label modal.
 */
export interface ILabelSize {
  id: string
  widthMm: number
  heightMm: number
}

export const LABEL_SIZES: ILabelSize[] = [
  { id: '30x40', widthMm: 30, heightMm: 40 },
  { id: '40x25', widthMm: 40, heightMm: 25 },
  { id: '40x30', widthMm: 40, heightMm: 30 },
  { id: '50x25', widthMm: 50, heightMm: 25 },
  { id: '50x30', widthMm: 50, heightMm: 30 },
  { id: '58x40', widthMm: 58, heightMm: 40 },
  { id: '60x40', widthMm: 60, heightMm: 40 },
  { id: '70x50', widthMm: 70, heightMm: 50 },
  { id: '80x50', widthMm: 80, heightMm: 50 },
  { id: '100x70', widthMm: 100, heightMm: 70 },
  { id: '100x100', widthMm: 100, heightMm: 100 },
]

/** The HPRT HT300's roll — the size the labels were designed at. */
export const DEFAULT_LABEL_SIZE =
  LABEL_SIZES.find((size) => size.id === '50x30') ?? LABEL_SIZES[0]

/**
 * Where the label sits on paper larger than itself — e.g. a wider roll, or a
 * printer that prints off-centre.
 */
export const LABEL_POSITIONS = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'center',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
] as const
export type TLabelPosition = (typeof LABEL_POSITIONS)[number]

/** Paper id meaning "the paper is exactly the label". */
export const PAPER_SAME_AS_LABEL = 'same'

const STORAGE_KEYS = {
  size: 'label_size',
  paper: 'label_paper',
  position: 'label_position',
} as const

function readStored(key: string) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Private mode or blocked storage — the choice lasts for this session.
  }
}

const findSize = (id: string | null) =>
  LABEL_SIZES.find((size) => size.id === id)

/** Paper the label fits on — at least as large in both directions. */
export const fitsOn = (label: ILabelSize, paper: ILabelSize) =>
  paper.widthMm >= label.widthMm && paper.heightMm >= label.heightMm

/**
 * The label roll, the paper it is printed on and where on that paper it sits —
 * picked in any label modal, remembered on this computer, shared by all of
 * them (they print on the same printer).
 */
export function useLabelPrintSettings() {
  const [size, setSize] = useState<ILabelSize>(
    () => findSize(readStored(STORAGE_KEYS.size)) ?? DEFAULT_LABEL_SIZE,
  )
  const [paperId, setPaperIdState] = useState<string>(
    () => readStored(STORAGE_KEYS.paper) ?? PAPER_SAME_AS_LABEL,
  )
  const [position, setPositionState] = useState<TLabelPosition>(() => {
    const stored = readStored(STORAGE_KEYS.position)
    return LABEL_POSITIONS.find((item) => item === stored) ?? 'center'
  })

  // A paper smaller than the label (e.g. after the label size changed) falls
  // back to the label itself.
  const storedPaper = findSize(paperId)
  const paper = storedPaper && fitsOn(size, storedPaper) ? storedPaper : size

  const setSizeId = useCallback((id: string) => {
    const next = findSize(id)
    if (!next) return
    setSize(next)
    writeStored(STORAGE_KEYS.size, next.id)
  }, [])

  const setPaperId = useCallback((id: string) => {
    if (id !== PAPER_SAME_AS_LABEL && !findSize(id)) return
    setPaperIdState(id)
    writeStored(STORAGE_KEYS.paper, id)
  }, [])

  const setPosition = useCallback((next: TLabelPosition) => {
    setPositionState(next)
    writeStored(STORAGE_KEYS.position, next)
  }, [])

  return {
    size,
    paper,
    /** Stored choice — `PAPER_SAME_AS_LABEL` or an `ILabelSize.id`. */
    paperId:
      storedPaper && fitsOn(size, storedPaper) ? paperId : PAPER_SAME_AS_LABEL,
    position,
    setSizeId,
    setPaperId,
    setPosition,
  }
}

const HORIZONTAL_FLEX = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
} as const
const VERTICAL_FLEX = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
} as const

/** Flex alignment (row direction) that puts the label at `position`. */
export function getLabelPositionFlex(position: TLabelPosition) {
  const [vertical, horizontal] = (
    position === 'center' ? ['middle', 'center'] : position.split('-')
  ) as [keyof typeof VERTICAL_FLEX, keyof typeof HORIZONTAL_FLEX]
  return {
    justifyContent: HORIZONTAL_FLEX[horizontal],
    alignItems: VERTICAL_FLEX[vertical],
  }
}

/** Size the labels' text and spacing were designed at. */
const DESIGN_WIDTH_MM = 50
const DESIGN_HEIGHT_MM = 30

export interface ILabelGeometry {
  widthMm: number
  heightMm: number
  paddingMm: number
  /** Bar height of the barcode. */
  barcodeHeightMm: number
  /** Room across the label for the barcode, quiet zones included. */
  barcodeMaxWidthMm: number
  /** A design size in points (as laid out at 50×30), scaled to this roll. */
  pt: (points: number) => string
  /** A design length in millimetres, scaled to this roll. */
  mm: (millimetres: number) => string
}

/**
 * Everything a label lays out by, for one roll. Text, spacing and bar height
 * grow or shrink with the roll from the 50×30 design; the barcode's bar width
 * is not scaled but re-fitted in whole printer dots (`fitModuleWidthMm`).
 */
export function getLabelGeometry(size: ILabelSize): ILabelGeometry {
  const scale = Math.min(
    size.widthMm / DESIGN_WIDTH_MM,
    size.heightMm / DESIGN_HEIGHT_MM,
  )
  const paddingMm = Math.max(1.2, 1.5 * scale)
  const round = (value: number) => Math.round(value * 100) / 100

  return {
    widthMm: size.widthMm,
    heightMm: size.heightMm,
    paddingMm,
    barcodeHeightMm: round(8 * scale),
    barcodeMaxWidthMm: size.widthMm - paddingMm * 2,
    pt: (points) => `${round(points * scale)}pt`,
    mm: (millimetres) => `${round(millimetres * scale)}mm`,
  }
}

/**
 * Stylesheet react-to-print injects into the print document, for the paper
 * the label is printed on. `@page size` pins the sheet to it — any other paper size makes the driver scale the
 * label, and the barcode bars with it, until the scanner cannot read them.
 */
export function getLabelPageStyle(size: ILabelSize) {
  return `
  @page {
    size: ${size.widthMm}mm ${size.heightMm}mm;
    margin: 0;
  }

  @media print {
    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      width: ${size.widthMm}mm;
      height: ${size.heightMm}mm;
      overflow: hidden;
    }

    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`
}

const PX_PER_MM = 96 / 25.4

/** On-screen magnification so the preview fills the given box; print stays 1:1. */
export function getLabelPreviewScale(
  size: ILabelSize,
  maxWidthPx = 360,
  maxHeightPx = 260,
) {
  return Math.min(
    maxWidthPx / (size.widthMm * PX_PER_MM),
    maxHeightPx / (size.heightMm * PX_PER_MM),
  )
}
